/**
 * Marketplace automation database import wiring.
 *
 * Marketplace ZIPs are published from the global `system` workspace. After
 * `ZipTransformer.importDocs` remaps docs into the user's workspace, database
 * host blocks must point at the imported root docs, not at the source
 * `db$system$...` ids embedded in the template.
 */
import type { Store } from '@blocksuite/affine/store';
import {
  createServerDatabaseRowShardDocId,
  type DatabaseShardManifest,
  normalizeDatabaseShardManifest,
} from '@streamlain/database-identity';

import {
  type DatabaseSystemDocAccessBinding,
  postDatabaseSystemDocAccessBindings,
} from '../../../modules/streamlain/file-import';

export type { DatabaseSystemDocAccessBinding };

type BlockLike = {
  readonly model: {
    readonly id: string;
    readonly props: Record<string, unknown>;
  };
};

type ImportedDatabaseRoot = {
  databaseId: string;
  rootDocId: string;
  manifest: DatabaseShardManifest;
};

export function repairImportedMarketplaceDatabaseReferences(
  stores: readonly Store[],
  workspaceId: string
): void {
  const rootsByDatabaseId = collectImportedDatabaseRoots(stores, workspaceId);

  for (const store of stores) {
    const databaseBlocks = getDatabaseBlocks(store);
    if (databaseBlocks.length === 0) continue;

    store.transact(() => {
      for (const block of databaseBlocks) {
        const props = block.model.props;
        repairImportedPublicationColumnBindings(props);
        if (rootsByDatabaseId.size === 0) continue;
        const databaseId = readString(props.databaseId);
        if (!databaseId) continue;

        const root = rootsByDatabaseId.get(databaseId);
        if (!root) continue;

        props.databaseRootDocId = root.rootDocId;
        const manifest = normalizeDatabaseShardManifest(
          props.databaseShardManifest
        );
        if (manifest) {
          props.databaseShardManifest = normalizeManifestForImport(
            manifest,
            workspaceId,
            root.rootDocId
          );
        }
      }
    });
  }
}

function repairImportedPublicationColumnBindings(
  props: Record<string, unknown>
): void {
  if (!Array.isArray(props.columns)) return;
  for (const rawColumn of props.columns) {
    if (!rawColumn || typeof rawColumn !== 'object') continue;
    const column = rawColumn as Record<string, unknown>;
    const columnId = readString(column.id);
    const data = column.data;
    if (!columnId || !data || typeof data !== 'object') continue;
    const publication = (data as Record<string, unknown>).publication;
    if (!publication || typeof publication !== 'object') continue;
    const binding = publication as Record<string, unknown>;
    if (!readString(binding.semanticKey)) {
      throw new Error(
        `Imported publication column "${columnId}" is missing semanticKey`
      );
    }
    binding.field = columnId;
  }
}

export function buildImportedMarketplaceDatabaseDocIdReplacements(
  stores: readonly Store[],
  workspaceId: string
): Map<string, string> {
  collectImportedDatabaseRoots(stores, workspaceId);
  return new Map<string, string>();
}

export function collectImportedMarketplaceDatabaseSystemDocAccessBindings(
  workspaceId: string,
  stores: readonly Store[]
): DatabaseSystemDocAccessBinding[] {
  const rootsByDatabaseId = collectImportedDatabaseRoots(stores, workspaceId);
  if (rootsByDatabaseId.size === 0) return [];

  const bindings: DatabaseSystemDocAccessBinding[] = [];
  for (const store of stores) {
    if (store.id.startsWith('db$')) continue;

    for (const block of getDatabaseBlocks(store)) {
      const props = block.model.props;
      const databaseId = readString(props.databaseId);
      const rootDocId = readString(props.databaseRootDocId);
      if (!databaseId || !rootDocId) continue;

      const root = rootsByDatabaseId.get(databaseId);
      if (!root || root.rootDocId !== rootDocId) continue;

      bindings.push({
        databaseId,
        rootDocId,
        accessDocId: store.id,
        blockId: block.model.id,
        manifest: root.manifest,
      });
    }
  }
  return bindings;
}

export async function registerImportedMarketplaceDatabaseSystemDocAccess(
  workspaceId: string,
  stores: readonly Store[],
  signal?: AbortSignal
): Promise<void> {
  const bindings = collectImportedMarketplaceDatabaseSystemDocAccessBindings(
    workspaceId,
    stores
  );
  await registerMarketplaceDatabaseSystemDocAccessBindings(
    workspaceId,
    bindings,
    signal
  );
}

export async function registerMarketplaceDatabaseSystemDocAccessBindings(
  workspaceId: string,
  bindings: readonly DatabaseSystemDocAccessBinding[],
  signal?: AbortSignal
): Promise<void> {
  if (bindings.length === 0) return;

  await postDatabaseSystemDocAccessBindings({
    workspaceId,
    bindings: [...bindings],
    signal,
  });
}

function collectImportedDatabaseRoots(
  stores: readonly Store[],
  workspaceId: string
): Map<string, ImportedDatabaseRoot> {
  const roots = new Map<string, ImportedDatabaseRoot>();
  for (const store of stores) {
    if (!store.id.startsWith('db$')) continue;

    for (const block of getDatabaseBlocks(store)) {
      const props = block.model.props;
      const databaseId = readString(props.databaseId);
      const manifest = normalizeDatabaseShardManifest(
        props.databaseShardManifest
      );
      if (!databaseId || !manifest) continue;

      roots.set(databaseId, {
        databaseId,
        rootDocId: store.id,
        manifest: normalizeManifestForImport(manifest, workspaceId, store.id),
      });
    }
  }
  return roots;
}

function normalizeManifestForImport(
  manifest: DatabaseShardManifest,
  workspaceId: string,
  rootDocId: string
): DatabaseShardManifest {
  return {
    ...manifest,
    workspaceId,
    databaseRootDocId: rootDocId,
    shards: manifest.shards.map(shard => ({
      ...shard,
      serverDocId: createServerDatabaseRowShardDocId({
        workspaceId,
        databaseId: manifest.databaseId,
        shardId: shard.shardId,
      }),
    })),
  };
}

function getDatabaseBlocks(store: Store): BlockLike[] {
  return store.getBlocksByFlavour('affine:database') as unknown as BlockLike[];
}

function readString(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null;
}
